import { PriorityFilterId } from '@records/shared/interfaces';
import { EntityType } from '@shared/models/entity-enum';
import { CustomInputConfig, TextInputConfig } from '@shared/models/icommons.model';

const CUSTOM_SALE_TEXT_INPUT_CONFIGS: TextInputConfig = {
  search: { isEnabled: true },
  clearButton: { isEnabled: true, restrictTyping: true },
  options: { isDisabled: false },
};

const CUSTOM_SALE_INPUT_CONFIGS: CustomInputConfig = {
  text: CUSTOM_SALE_TEXT_INPUT_CONFIGS,
};

/**
 * COUNTRY_MAP has the list of countries which has userCountries as the key and country codes as the value.
 * This is used when a user is searching for companies/contacts using Company/Contact dialog.
 */
// COUNTRY_MAP moved to @miq/shared-models (L2-P4 leaf). Re-exported for compat.
export { COUNTRY_MAP } from '@miq/shared-models';

/**
 * COUNTRIES_INCLUDE_EMPTY is a list of countries for which we have to include (empty) as a selected value by default.
 * Users in US want to search for companies/contacts which have no location/postal_address.country as null by default.
 * Whereas other countries doesn't want this. So we are adding a value to the existing countries and selecting it by
 * default to the countries in this list.
 */
export const COUNTRIES_INCLUDE_EMPTY = ['United States of America'];
export const CUSTOM_INPUT_CONFIGS: { [entity: string]: CustomInputConfig } = {
  sale: CUSTOM_SALE_INPUT_CONFIGS,
};

/**
 * Market, Submarket, and Type IDs for prioritized positioning of search filters
 */
export const PRIORITY_FILTER_IDS_BY_ENTITY: { [entity in EntityType]?: PriorityFilterId } = {
  [EntityType.Availability]: {
    5: { isPriority: true, priorityLevel: 2 }, // Space Usage Type
    317: { isPriority: true, priorityLevel: 1 }, // Market
    318: { isPriority: true, priorityLevel: 3 }, // Submarket
  },
  [EntityType.Brand]: {},
  [EntityType.Company]: {
    1319: { isPriority: true, priorityLevel: 1 }, // Market
    1320: { isPriority: true, priorityLevel: 2 }, // Submarket
  },
  [EntityType.Contact]: {
    17062: { isPriority: true, priorityLevel: 1 }, // Market
    17063: { isPriority: true, priorityLevel: 2 }, // Submarket
  },
  [EntityType.LeaseComp]: {
    1604: { isPriority: true, priorityLevel: 2 }, // Space Usage Type
    1704: { isPriority: true, priorityLevel: 1 }, // Market
    1705: { isPriority: true, priorityLevel: 3 }, // Submarket
  },
  [EntityType.PortfolioSale]: {
    16988: { isPriority: true, priorityLevel: 1 }, // Market
    17024: { isPriority: true, priorityLevel: 2 }, // Space Usage Type
    17064: { isPriority: true, priorityLevel: 3 }, // Submarket
  },
  [EntityType.Property]: {
    860: { isPriority: true, priorityLevel: 4 }, // Division
    861: { isPriority: true, priorityLevel: 1 }, // Market
    862: { isPriority: true, priorityLevel: 3 }, // Submarket
    842: { isPriority: true, priorityLevel: 2 }, // Type
    863: { isPriority: true, priorityLevel: 5 }, // District
    864: { isPriority: true, priorityLevel: 6 }, // Neighborhood
    3882: { isPriority: true, priorityLevel: 7 }, // Road Corridor
    16925: { isPriority: true, priorityLevel: 8 }, // Market Area
    16996: { isPriority: true, priorityLevel: 9 }, // Local Market Area
  },
  [EntityType.SaleComp]: {
    2154: { isPriority: true, priorityLevel: 1 }, // Market
    2155: { isPriority: true, priorityLevel: 3 }, // Submarket
    2020: { isPriority: true, priorityLevel: 2 }, // Space Usage Type
  },
  [EntityType.Tenancy]: {
    3790: { isPriority: true, priorityLevel: 2 }, // Property Type
    3799: { isPriority: true, priorityLevel: 1 }, // Market
    3801: { isPriority: true, priorityLevel: 3 }, // Submarket
  },
  [EntityType.Tim]: {
    16057: { isPriority: true, priorityLevel: 2 }, // Property Type
    16130: { isPriority: true, priorityLevel: 5 }, // Target Division
    16131: { isPriority: true, priorityLevel: 1 }, // Target Market
    16137: { isPriority: true, priorityLevel: 3 }, // Target Submarket
    16138: { isPriority: true, priorityLevel: 7 }, // Target District
    16139: { isPriority: true, priorityLevel: 8 }, // Target Neighborhood
    16354: { isPriority: true, priorityLevel: 4 }, // Target Global Region
    17001: { isPriority: true, priorityLevel: 6 }, // Target Local Market Area
  },
  [EntityType.Activity]: {
    // Priority filter IDs to be populated once Activity catalog column IDs are confirmed from /lookup/filters API
    19633: { isPriority: true, priorityLevel: 1 }, // Activity Type
    19638: { isPriority: true, priorityLevel: 3 }, // Activity ID
    19639: { isPriority: true, priorityLevel: 2 }, // Created date
  },
};

export const Countries_Default_Space_UOM = {
  Australia: 'Square Meter',
  Austria: 'Square Meter',
  Belgium: 'Square Meter',
  China: 'Square Meter',
  'Czech Republic': 'Square Meter',
  Denmark: 'Square Meter',
  Finland: 'Square Meter',
  France: 'Square Meter',
  Germany: 'Square Meter',
  'Hong Kong, SAR China': 'Square Foot',
  Hungary: 'Square Meter',
  India: 'Square Foot',
  Italy: 'Square Meter',
  Luxembourg: 'Square Meter',
  Netherlands: 'Square Meter',
  'New Zealand': 'Square Meter',
  Norway: 'Square Meter',
  Poland: 'Square Meter',
  Portugal: 'Square Meter',
  Romania: 'Square Meter',
  Serbia: 'Square Meter',
  Slovakia: 'Square Meter',
  Spain: 'Square Meter',
  Sweden: 'Square Meter',
  Switzerland: 'Square Meter',
  'United Kingdom': 'Square Foot',
  'United States of America': 'Square Foot',
};
